import { isPlatformBrowser, registerLocaleData } from "@angular/common";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import localePt from "@angular/common/locales/pt";
import { APP_INITIALIZER, Inject, LOCALE_ID, NgModule, PLATFORM_ID } from "@angular/core";
import { FlexLayoutModule } from "@angular/flex-layout";
import {
  MatBadgeModule,
  MatButtonModule,
  MatIconModule,
  MatIconRegistry,
  MatMenuModule,
  MatRippleModule,
  MatSnackBarModule,
  MatTabsModule,
  MatToolbarModule,
  MatTooltipModule,
  MAT_DATE_LOCALE,
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
} from "@angular/material";
import { MatMomentDateModule } from "@angular/material-moment-adapter";
import { BrowserModule, DomSanitizer } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ServiceWorkerModule } from "@angular/service-worker";
import {
  downgradeComponent,
  downgradeInjectable,
  setAngularJSGlobal,
  UpgradeModule,
} from "@angular/upgrade/static";
import { APIConfig, TokenProviderService } from "src/app/api";
import { PopoverModule } from "@mandae/ng-popover";
import {
  GooglePlacesModule,
  GooglePlacesModuleConfig,
} from "@mandae/ng-utils/google-places";
import * as angular from "angular";
import { AppModule as AngularJSAppModule } from "../angularjs-app/main";
import { environment } from "../environments/environment";
import { AJSMainDirective } from "./app-upgrade.component";
import { AppComponent } from "./app.component";
import { CollectionsModule } from "./collections/collections.module";
import { DialogsModule } from "./dialogs/dialogs.module";
import { FinancialModule } from "./financial/financial.module";
import { HelpFabComponent } from "./help-fab/help-fab.component";
import { HomeModule } from "./home/home.module";
import { ICON_GROUPS } from "./icons";
import { IndicatorsModule } from "./indicators/indicators.module";
import { OccurrencesModule } from "./occurrences/occurrences.module";
import { PackagesModule } from "./packages/packages.module";
import { ProfileModule } from "./profile/profile.module";
import { ReverseModule } from "./reverse/reverse.module";
import { ServicesModule } from "./services/services.module";
import { ShippingReturnModule } from "./shipping-return/shipping-return.module";
import { SideMenuComponent } from "./side-menu/side-menu.component";
import { ToolbarComponent } from "./toolbar/toolbar.component";
import { SelectTourDialogComponent } from "./tour/select-tour-dialog/select-tour-dialog.component";
import { TourService } from "./tour/tour.service";
import { TutorialModule } from "./tutorial/tutorial.module";
import { RouterModule } from "@angular/router";
import { HttpAuthInterceptor } from "./interceptor";
import { loadAwsWafSdk } from "./aws-waf/aws-waf.util";
import { RelatoriosModule } from "./relatorios/relatorios.module";
import { RegraFreteModule } from "./regra-frete/regra-frete.module";
import { SimuladorFreteModule } from "./simulador-frete/simulador-frete.module";
import { ZendeskMessengerComponent } from './zendesk-messenger/zendesk-messenger.component';
import { ZendeskWidgetComponent } from './zendesk-widget/zendesk-widget.component';
import { ZendeskService } from './zendesk.service';
import { LocalStorageService } from './local-storage.service';
registerLocaleData(localePt, "pt");

angular
  .module("ajs-bootstrap", [AngularJSAppModule])
  .component("mandaeBootstrap", { template: "<mandae-root></mandae-root>" })
  .directive("mandaeRoot", downgradeComponent({ component: AppComponent }))
  .directive(
    "appSideMenu",
    downgradeComponent({ component: SideMenuComponent })
  )
  .directive("appToolbar", downgradeComponent({ component: ToolbarComponent }));

export interface ApiConfig {
  customerApiUrl: string;
  financeiroApiUrl: string;
  loginApiUrl: string;
  pedidoApiUrl: string;
  rastreamentoApiUrl: string;
  destinatarioApiUrl: string;
  atendimentoApiUrl: string;
}

const urls: ApiConfig = {
  customerApiUrl: environment.angularJSconstants.URLAPICLIENTE,
  financeiroApiUrl: environment.angularJSconstants.URL_FINANCEIRO,
  loginApiUrl: environment.angularJSconstants.URLAPILOGIN,
  pedidoApiUrl: environment.angularJSconstants.URLAPIPEDIDO,
  rastreamentoApiUrl: environment.angularJSconstants.URL_RASTREAMENTO,
  destinatarioApiUrl: environment.angularJSconstants.URLAPIDESTINATARIO,
  atendimentoApiUrl: environment.angularJSconstants.URL_API_ATENDIMENTO,
};

@NgModule({
  declarations: [
    AppComponent,
    AJSMainDirective,
    HelpFabComponent,
    SelectTourDialogComponent,
    SideMenuComponent,
    ToolbarComponent,
    ZendeskMessengerComponent,
    ZendeskWidgetComponent,
  ],
  imports: [
    BrowserModule,
    UpgradeModule,
    HttpClientModule,
    ServicesModule,
    BrowserAnimationsModule,
    GooglePlacesModule,
    FlexLayoutModule,
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: environment.enableSW,
    }),

    DialogsModule,
    TutorialModule,
    PopoverModule,

    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatMomentDateModule,
    MatRippleModule,
    MatSnackBarModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,

    // TODO: when migration is complete, import this module
    // AppRoutingModule,
    RouterModule,

    // Modules in migration from AngularJS -- those should be removed when finished migration and use lazy-loading with routes
    CollectionsModule,
    FinancialModule,
    HomeModule,
    IndicatorsModule,
    OccurrencesModule,
    PackagesModule,
    ReverseModule,
    ShippingReturnModule,
    ProfileModule,
    RelatoriosModule,
    RegraFreteModule,
    SimuladorFreteModule,
  ],
  entryComponents: [
    AppComponent,
    SelectTourDialogComponent,
    SideMenuComponent,
    ToolbarComponent,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: "pt",
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: "pt",
    },
    {
      provide: APIConfig,
      useValue: new APIConfig(urls),
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: "outline",
      },
    },
    {
      provide: GooglePlacesModuleConfig,
      useValue: new GooglePlacesModuleConfig(environment.googleMapsApiKey),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpAuthInterceptor,
      multi: true,
    },
    {
      // Carrega o SDK de integração do AWS WAF antes do bootstrap.
      provide: APP_INITIALIZER,
      useFactory: () => loadAwsWafSdk,
      multi: true,
    },
      ZendeskService,
      LocalStorageService
  ],
})
export class AppModule {
  constructor(
    private upgrade: UpgradeModule,
    registry: MatIconRegistry,
    sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) platformId: any
  ) {
    Object.keys(ICON_GROUPS).forEach((groupName) => {
      const group = ICON_GROUPS[groupName];
      if (isPlatformBrowser(platformId)) {
        group.forEach((icon) =>
          registry.addSvgIconInNamespace(
            groupName,
            icon,
            sanitizer.bypassSecurityTrustResourceUrl(
              `../assets/icons/${groupName}/${icon}.svg`
            )
          )
        );
      } else {
        group.forEach((icon) =>
          registry.addSvgIconLiteralInNamespace(
            groupName,
            icon,
            sanitizer.bypassSecurityTrustHtml('<svg viewBox="0 0 24 24"></svg>')
          )
        );
      }
    });
  }

  ngDoBootstrap() {
    setAngularJSGlobal(angular);
    this.upgrade.bootstrap(document.documentElement, ["ajs-bootstrap"]);
  }
}

try {
  angular
    .module("mandaeApp")
    .factory("$appTokenProvider", downgradeInjectable(TokenProviderService))
    .factory("$tour", downgradeInjectable(TourService));
} catch (e) {}
